import { configureStore } from "@reduxjs/toolkit";
import { orderManagementSlice } from "./slices/orderManagementSlice";
import { userProfileSlice } from "./slices/userProfileSlice";
import { audioAICreativeSlice } from "./slices/audioAICreative";
import { googlePlacesAutoCompleteSlice } from "./slices/googlePlacesAutoCompleteSlice";
import { unsubscribeEmailSlice } from "./slices/unsubscribeEmail";
import { emailTemplateSlice } from "./slices/emailTemplate";
import { impersonationSlice } from "./slices/impersonationSlice";
import { twoMFASlice } from "./slices/twoMFASlice";
import { sessionUserReducer, userApiSlice } from "./slices/userDataSlice";
import { persistStore } from "redux-persist";
import { doohSlice } from "./slices/doohSlice";
import { agencyFiltersPersistentReducer } from "./slices/filterSlice";
import { twoFAPersistentReducer } from "./slices/twoFASliceState";
import doohDataSlice from "./slices/doohDataSlice";
import { userDashboardSlice } from "./slices/userDashboardSlice";
import { dashboardFiltersPersistentReducer } from "./slices/userDashboardFiltersSlice";
import { whitelabelConfigReducer, whitelabelConfigsApiSlice } from "./slices/whitelabelSlice";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { trafficEstimatorApiSlice } from "./slices/trafficEstimatorSlice";
import { creativeBuilderApiSlice } from "./slices/creativeBuilderSlice";
import { rtbNetworkApiSlice } from "./slices/oRtbNetworkSlice";
import { campaignCsvReportingApiSlice } from "./slices/campaignCsvReportingSlice";
import { accountManagementApiSlice } from "./slices/accountManagementSlice";
import { urlParameterReducer } from "./slices/urlParameterSlice";
import { agencyFileManagerSlice } from "./slices/agencyFileManagerSlice";
import agencySlice from "./slices/agencySlice"; // Import the new agencySlice
import overlayLoaderSlice from "./slices/overlayLoaderSlice";
import { billingReportSlice } from "./slices/billingReportSlice";
import { renewalReportingSlice } from "./slices/renewalReporting";
import resetSlice from "./slices/resetSlice";
import appStateSlice, { appStateApiSlice } from "./slices/appStateSlice";

export const store = configureStore({
	reducer: {
		[orderManagementSlice.reducerPath]: orderManagementSlice.reducer,
		[userProfileSlice.reducerPath]: userProfileSlice.reducer,
		[billingReportSlice.reducerPath]: billingReportSlice.reducer,
		[renewalReportingSlice.reducerPath]: renewalReportingSlice.reducer,
		[audioAICreativeSlice.reducerPath]: audioAICreativeSlice.reducer,
		[unsubscribeEmailSlice.reducerPath]: unsubscribeEmailSlice.reducer,
		[googlePlacesAutoCompleteSlice.reducerPath]: googlePlacesAutoCompleteSlice.reducer,
		[emailTemplateSlice.reducerPath]: emailTemplateSlice.reducer,
		[impersonationSlice.reducerPath]: impersonationSlice.reducer,
		[twoMFASlice.reducerPath]: twoMFASlice.reducer,
		[agencyFileManagerSlice.reducerPath]: agencyFileManagerSlice.reducer,
		[userApiSlice.reducerPath]: userApiSlice.reducer,
		[doohSlice.reducerPath]: doohSlice.reducer,
		[userDashboardSlice.reducerPath]: userDashboardSlice.reducer,
		[whitelabelConfigsApiSlice.reducerPath]: whitelabelConfigsApiSlice.reducer,
		[trafficEstimatorApiSlice.reducerPath]: trafficEstimatorApiSlice.reducer,
		[creativeBuilderApiSlice.reducerPath]: creativeBuilderApiSlice.reducer,
		[rtbNetworkApiSlice.reducerPath]: rtbNetworkApiSlice.reducer,
		[campaignCsvReportingApiSlice.reducerPath]: campaignCsvReportingApiSlice.reducer,
		[accountManagementApiSlice.reducerPath]: accountManagementApiSlice.reducer,
		[appStateApiSlice.reducerPath]: appStateApiSlice.reducer,
		whitelabelConfigs: whitelabelConfigReducer,
		sessionUser: sessionUserReducer,
		filter: agencyFiltersPersistentReducer,
		twoFAState: twoFAPersistentReducer,
		doohData: doohDataSlice,
		dashboardFilters: dashboardFiltersPersistentReducer,
		urlParameter: urlParameterReducer,
		agency: agencySlice,
		overlay: overlayLoaderSlice,
		reset: resetSlice,
		appState: appStateSlice,
	},
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware().concat(
			orderManagementSlice.middleware,
			userProfileSlice.middleware,
			audioAICreativeSlice.middleware,
			unsubscribeEmailSlice.middleware,
			googlePlacesAutoCompleteSlice.middleware,
			emailTemplateSlice.middleware,
			impersonationSlice.middleware,
			twoMFASlice.middleware,
			userApiSlice.middleware,
			doohSlice.middleware,
			userDashboardSlice.middleware,
			whitelabelConfigsApiSlice.middleware,
			trafficEstimatorApiSlice.middleware,
			creativeBuilderApiSlice.middleware,
			rtbNetworkApiSlice.middleware,
			campaignCsvReportingApiSlice.middleware,
			accountManagementApiSlice.middleware,
			agencyFileManagerSlice.middleware,
			billingReportSlice.middleware,
			renewalReportingSlice.middleware,
			appStateApiSlice.middleware,
		),
});

export const persistentStore = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
